/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useRef, useEffect, useState } from 'react';

// Mantine
import { Drawer, Box, ScrollArea } from '@mantine/core';
import { useForm, isNotEmpty } from '@mantine/form';

// Styles
import classes from './AddSite.module.css';

// Components
import { PreviousDrawerButton } from '@components/atoms/PreviousDrawerButton';
import { DefaultModal } from '@components/molecules/DefaultModal';

// Components : Steps
import { AddSiteForm } from './AddSiteForm';

// Store
import useStore from '@store/index';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const AddSite = (props: {
	onClick?: () => void;
	onClose: () => void;
	opened: boolean;
}) => {
	/* ******************************** STATES ******************************* */
	const [isOpenModalCancel, setIsOpenModalCancel] =
		useState(false);
	/* ******************************** HOOKS *********************************** */
	const viewport = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();
	const location = useLocation();
	const { projectId } = useParams();

	const {
		offsetCreationActiveStep,
		setOffsetCreationActiveStep,
		setParamsFromProject,
		paramsFromProject,
	} = useStore();

	// Validators
	const form = useForm({
		initialValues: {
			project: '',
			organization: '',
			entity: '',
			name: '',
			minValue: 0,
			maxValue: 0,
			country: '',
			place: '',
			ratio: 0,
			startDate: null,
		},

		validate: {
			entity: isNotEmpty(t('global.entityRequired')),
			organization: isNotEmpty(t('global.siteRequired')),
			project: isNotEmpty(t('global.projectRequired')),
			name: (value) =>
				value.length < 2
					? t('errors.nameMustHaveAtLeast2Letters')
					: null,
			ratio: (value, values) =>
				values.startDate !== null && values.ratio === 0
					? t('global.ratioRequired')
					: null,
			startDate: (value, values) =>
				values.ratio !== 0 && values.startDate === null
					? t('errors.startDateIsRequired')
					: null,
			place: (value, values) =>
				values.country !== '' &&
				values.country !== null &&
				value.length < 2
					? t('errors.placeMustHaveAtLeast2Letters')
					: null,
			minValue: (value, values) =>
				value >= values.maxValue &&
				value !== 0 &&
				values.maxValue !== 0
					? t('global.minValueLessThanMaxValue')
					: null,
			maxValue: (value, values) =>
				value <= values.minValue &&
				value !== 0 &&
				values.minValue !== 0
					? t('global.maxValueGreaterThanMaxValue')
					: null,
		},
	});

	useEffect(() => {
		if (
			offsetCreationActiveStep >= 0 &&
			viewport &&
			viewport.current
		) {
			viewport.current!.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}
	}, [offsetCreationActiveStep]);

	useEffect(() => {
		setOffsetCreationActiveStep(0);
	}, []);

	useEffect(() => {
		if (
			paramsFromProject &&
			paramsFromProject.producer &&
			paramsFromProject.project &&
			paramsFromProject.site
		) {
			form.setFieldValue(
				'entity',
				paramsFromProject.producer
			);
			form.setFieldValue(
				'project',
				paramsFromProject.project
			);
			form.setFieldValue('site', paramsFromProject.site);
		}
	}, [paramsFromProject]);

	const isPreviousMeasurabilityProjectDetails =
		location.pathname.split('/')[1] ===
		'measurability-projects';

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer.Root
			position="right"
			size="100%"
			opened={props.opened}
			onClose={() => {
				setIsOpenModalCancel(true);
			}}
		>
			<Drawer.Overlay />
			<Drawer.Content
				ref={viewport}
				className={classes.root}
			>
				<DefaultModal
					opened={isOpenModalCancel}
					onConfirm={() => {
						props.onClose();
						setIsOpenModalCancel(false);
						setParamsFromProject({});
						form.reset();
					}}
					onClose={() => {
						setIsOpenModalCancel(false);
					}}
					title={
						projectId
							? t('sites.backToProjectDetailsSure')
							: t('sites.backToAllSitesSure')
					}
				/>
				{/** Header */}
				<Drawer.Header className={classes.header}>
					<PreviousDrawerButton
						onClick={() => {
							setIsOpenModalCancel(true);
						}}
					>
						{projectId
							? t('sites.backToProjectDetails')
							: t('sites.backToAllSites')}
					</PreviousDrawerButton>
				</Drawer.Header>
				{/** Body */}
				<Drawer.Body className={classes.body}>
					<Box className={classes.bodySpacer} />
					<Box className={classes.bodyContent}>
						{/** Content */}
						<ScrollArea
							viewportRef={viewport}
							className={classes.containerContent}
						>
							<AddSiteForm
								isPreviousMeasurabilityProjectDetails={
									isPreviousMeasurabilityProjectDetails
								}
								form={form}
								onCancel={() => {
									setIsOpenModalCancel(true);
								}}
								onBack={() => {
									props.onClose();
									form.reset();
								}}
							/>
						</ScrollArea>
					</Box>
					<Box className={classes.bodySpacer} />
				</Drawer.Body>
				{/** Footer */}
				<Box className={classes.containerFooter}>
					<Box className={classes.spacerFooter} />

					<Box className={classes.spacerFooter} />
				</Box>
			</Drawer.Content>
		</Drawer.Root>
	);
};
